import { getStoredLegacyToken } from './legacyAuthService';

export type RegisteredUserCategory = 'utente' | 'amministratore' | 'superadmin' | 'anonimo';
export type RegisteredUserSource = 'mobile_app' | 'creator_request' | 'creator_activated' | 'combined' | 'legacy_access';
export type RegisteredUserScope = 'all' | 'app' | 'creator' | 'legacy';
export type RegisteredUserStatus = 'all' | 'active_app_user' | 'pending' | 'approved' | 'activated' | 'rejected' | 'legacy_access';

export interface RegisteredUserItem {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled: boolean;
  isSuper: boolean;
  guarded: boolean;
  anonymous: boolean;
  category?: RegisteredUserCategory;
  source?: RegisteredUserSource;
  organizationId?: number | null;
  organizationName?: string;
  createdAt?: string | null;
  lastSeenAt?: string | null;
  registrationStatus?: RegisteredUserStatus | 'unknown';
  platforms?: string[];
  sessionCount?: number;
  eventCount?: number;
  manageable?: boolean;
}

export interface RegisteredUsersSummary {
  total: number;
  registered: number;
  anonymous: number;
  enabled: number;
  disabled: number;
  amministratori?: number;
  superadmin?: number;
  utenti?: number;
  merged?: number;
  fromUsers?: number;
  fromAuthUsers?: number;
  appUsers?: number;
  creatorLeads?: number;
  creatorPending?: number;
  creatorApproved?: number;
  creatorActivated?: number;
  creatorRejected?: number;
  combined?: number;
  activeLast30d?: number;
  legacyAccess?: number;
}

export interface RegisteredUsersMeta {
  dataOrigin: 'firestore' | 'legacy' | 'modern_registry';
  page?: number;
  pageSize?: number;
  total: number;
  filteredTotal?: number;
  totalPages?: number;
  unfilteredTotal?: number;
  snapshotAt?: string | null;
  filters?: Record<string, unknown>;
}

export interface RegisteredUsersOrganization {
  id: number;
  name: string;
}

export interface RegisteredUsersTrendPoint {
  date: string;
  label: string;
  total: number;
}

export interface CreateRegisteredAdminUserPayload {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  temporaryPassword: string;
  organizationId?: number;
  organizationName?: string;
  createOrganization?: boolean;
  note?: string;
}

export interface ListRegisteredUsersParams {
  searchText?: string;
  scope?: RegisteredUserScope;
  size?: number;
  category?: 'all' | RegisteredUserCategory;
  source?: 'all' | RegisteredUserSource;
  status?: RegisteredUserStatus;
  organizationId?: number | 'all';
  createdFrom?: string;
  createdTo?: string;
  lastSeenFrom?: string;
  lastSeenTo?: string;
  sortBy?: 'createdAt' | 'lastSeenAt' | 'username' | 'organization' | 'category' | 'source';
  sortDir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

function buildHeaders(): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  const rawToken =
    getStoredLegacyToken()
    || localStorage.getItem('legacy_token')
    || sessionStorage.getItem('legacy_token')
    || '';

  if (rawToken) {
    headers.Authorization = /^Bearer\s+/i.test(rawToken) ? rawToken : `Bearer ${rawToken}`;
  }

  return headers;
}

async function parseResponse<T>(response: Response): Promise<T> {
  const rawText = await response.text();
  let payload: unknown = null;
  try {
    payload = rawText ? JSON.parse(rawText) : null;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const structuredError =
      (payload && typeof payload === 'object' && 'error' in payload && typeof (payload as { error?: unknown }).error === 'string')
        ? String((payload as { error: string }).error)
        : '';
    const message = structuredError || rawText || `API error (${response.status})`;
    throw new Error(message);
  }

  return (payload as T) || ({} as T);
}

export async function listRegisteredUsers(params?: ListRegisteredUsersParams): Promise<{
  success: boolean;
  users: RegisteredUserItem[];
  summary: RegisteredUsersSummary;
  globalSummary?: RegisteredUsersSummary;
  organizations?: RegisteredUsersOrganization[];
  meta?: RegisteredUsersMeta;
  trend?: RegisteredUsersTrendPoint[];
}> {
  const query = new URLSearchParams();
  if (params?.searchText) query.set('searchText', params.searchText);
  if (params?.scope) query.set('scope', params.scope);
  if (params?.size) query.set('size', String(params.size));
  if (params?.category && params.category !== 'all') query.set('category', params.category);
  if (params?.source && params.source !== 'all') query.set('source', params.source);
  if (params?.status && params.status !== 'all') query.set('status', params.status);
  if (params?.organizationId && params.organizationId !== 'all') query.set('organizationId', String(params.organizationId));
  if (params?.createdFrom) query.set('createdFrom', params.createdFrom);
  if (params?.createdTo) query.set('createdTo', params.createdTo);
  if (params?.lastSeenFrom) query.set('lastSeenFrom', params.lastSeenFrom);
  if (params?.lastSeenTo) query.set('lastSeenTo', params.lastSeenTo);
  if (params?.sortBy) query.set('sortBy', params.sortBy);
  if (params?.sortDir) query.set('sortDir', params.sortDir);
  if (params?.page) query.set('page', String(params.page));
  if (params?.pageSize) query.set('pageSize', String(params.pageSize));

  const suffix = query.toString() ? `?${query.toString()}` : '';
  const response = await fetch(`/api/admin-users/list${suffix}`, {
    method: 'GET',
    headers: buildHeaders(),
  });

  return parseResponse<{
    success: boolean;
    users: RegisteredUserItem[];
    summary: RegisteredUsersSummary;
    globalSummary?: RegisteredUsersSummary;
    organizations?: RegisteredUsersOrganization[];
    meta?: RegisteredUsersMeta;
    trend?: RegisteredUsersTrendPoint[];
  }>(response);
}

export async function deleteRegisteredUser(uid: string): Promise<{ success: boolean }> {
  const response = await fetch('/api/admin-users/delete', {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify({ uid }),
  });

  return parseResponse<{ success: boolean }>(response);
}

export async function updateRegisteredUserStatus(uid: string, enabled: boolean): Promise<{ success: boolean }> {
  const response = await fetch('/api/admin-users/status', {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify({ uid, enabled }),
  });

  return parseResponse<{ success: boolean }>(response);
}

export async function createRegisteredAdminUser(
  payload: CreateRegisteredAdminUserPayload,
): Promise<{ success: boolean; userId?: number | string | null }> {
  const response = await fetch('/api/admin-users/create', {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });
  return parseResponse<{ success: boolean; userId?: number | string | null }>(response);
}
